import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { BaseElement } from "../utils/ui-components.js";

import "../components/header-component.js";
import "../components/footer-component.js";
import "../components/api-docs/api-docs-code-block.js";

@customElement("cli-docs-page")
export class CliDocsPage extends BaseElement {
    render() {
        return html`
            <div class="flex flex-col min-h-screen container max-w-6xl p-4 md:p-6 lg:p-8">
                <header-component></header-component>

                <h1 class="text-3xl font-bold">CLI Documentation</h1>
                <p class="mt-2 text-base-content/60">
                    A command-line interface for the
                    <a href="/docs/api" class="link link-primary">AppShovel API</a>. Fetch keyword popularity scores and recommendations from your
                    terminal.
                </p>

                <main class="flex-1 min-w-0 space-y-12 mt-8">
                    ${this.renderInstallation()} ${this.renderAuthentication()} ${this.renderPopularities()} ${this.renderRecommendations()}
                    ${this.renderCountriesPlatforms()} ${this.renderOutputFormats()} ${this.renderGlobalOptions()}
                </main>

                <div class="grow"></div>
                <footer-component></footer-component>
            </div>
        `;
    }

    private renderInstallation() {
        return html`
            <section class="space-y-4">
                <h2 class="text-2xl font-bold">Installation</h2>
                <p>Install globally from npm:</p>
                <api-docs-code-block .code=${`npm install -g appshovel`}></api-docs-code-block>
                <p>Or run directly without installing:</p>
                <api-docs-code-block .code=${`npx appshovel popularities "weather,fitness"`}></api-docs-code-block>
            </section>
        `;
    }

    private renderAuthentication() {
        return html`
            <section class="space-y-4">
                <div class="divider"></div>
                <h2 class="text-2xl font-bold">Authentication</h2>
                <p>
                    Create an API key on the <a href="/settings" class="link link-primary">Settings</a> page, then set it as an environment variable:
                </p>
                <api-docs-code-block .code=${`export APPSHOVEL_API_KEY="your_api_key"`}></api-docs-code-block>
                <p>
                    You can also pass it per-command with <code class="badge badge-ghost">--api-key</code>, but the environment variable is preferred
                    — it keeps secrets out of your shell history.
                </p>
                <api-docs-code-block .code=${`appshovel popularities "weather" --api-key your_api_key`}></api-docs-code-block>
            </section>
        `;
    }

    private renderPopularities() {
        return html`
            <section class="space-y-4">
                <div class="divider"></div>
                <h2 class="text-2xl font-bold">popularities</h2>
                <p>Fetch popularity and difficulty scores for up to 100 keywords at once. Pass comma-separated terms as a single argument.</p>

                <api-docs-code-block .code=${`appshovel popularities "weather,fitness,tracker" --country us --platform iphone`}></api-docs-code-block>

                <h3 class="text-lg font-semibold">Output</h3>
                <api-docs-code-block
                    .code=${`Country: United States | Platform: iPhone

| Term    | Popularity | Difficulty |
| ------- | ---------- | ---------- |
| weather | 82         | 95         |
| fitness | 55         | 61         |
| tracker | 38         | 42         |`}
                ></api-docs-code-block>

                <h3 class="text-lg font-semibold">Short flags</h3>
                <api-docs-code-block .code=${`appshovel popularities "weather,fitness" -c de -p ipad -f json`}></api-docs-code-block>
            </section>
        `;
    }

    private renderRecommendations() {
        return html`
            <section class="space-y-4">
                <div class="divider"></div>
                <h2 class="text-2xl font-bold">recommendations</h2>
                <p>Get keyword recommendations for a single term.</p>

                <api-docs-code-block .code=${`appshovel recommendations "weather" --country us --platform iphone`}></api-docs-code-block>

                <h3 class="text-lg font-semibold">Output</h3>
                <api-docs-code-block
                    .code=${`Term: weather | Country: United States | Platform: iPhone

| Recommendation   | Popularity |
| ---------------- | ---------- |
| weather forecast | 70         |
| weather radar    | 65         |
| weather app      | 58         |`}
                ></api-docs-code-block>
            </section>
        `;
    }

    private renderCountriesPlatforms() {
        return html`
            <section class="space-y-4">
                <div class="divider"></div>
                <h2 class="text-2xl font-bold">countries & platforms</h2>
                <p>List all supported country codes and platforms.</p>

                <api-docs-code-block .code=${`appshovel countries`}></api-docs-code-block>
                <api-docs-code-block .code=${`appshovel platforms`}></api-docs-code-block>

                <p>
                    Both commands support <code class="badge badge-ghost">--format json</code> and
                    <code class="badge badge-ghost">--format csv</code>.
                </p>
            </section>
        `;
    }

    private renderOutputFormats() {
        return html`
            <section class="space-y-4">
                <div class="divider"></div>
                <h2 class="text-2xl font-bold">Output Formats</h2>
                <p>All commands support three output formats via <code class="badge badge-ghost">--format</code>:</p>

                <div class="overflow-x-auto">
                    <table class="table table-sm">
                        <thead>
                            <tr>
                                <th>Format</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>md</code></td>
                                <td>Markdown table with human-readable country/platform names (default)</td>
                            </tr>
                            <tr>
                                <td><code>json</code></td>
                                <td>Raw JSON matching the API response shape</td>
                            </tr>
                            <tr>
                                <td><code>csv</code></td>
                                <td>CSV with headers</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <api-docs-code-block
                    .code=${`# JSON for scripting and piping
appshovel popularities "weather" --format json

# CSV for spreadsheets
appshovel popularities "weather" --format csv`}
                ></api-docs-code-block>
            </section>
        `;
    }

    private renderGlobalOptions() {
        return html`
            <section class="space-y-4">
                <div class="divider"></div>
                <h2 class="text-2xl font-bold">Global Options</h2>
                <p>These options apply to all commands.</p>

                <div class="overflow-x-auto">
                    <table class="table table-sm">
                        <thead>
                            <tr>
                                <th>Option</th>
                                <th>Short</th>
                                <th>Default</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>--api-key &lt;key&gt;</code></td>
                                <td><code>-k</code></td>
                                <td><code>APPSHOVEL_API_KEY</code> env var</td>
                                <td>API key for authentication</td>
                            </tr>
                            <tr>
                                <td><code>--country &lt;code&gt;</code></td>
                                <td><code>-c</code></td>
                                <td><code>us</code></td>
                                <td>Country code (case-insensitive)</td>
                            </tr>
                            <tr>
                                <td><code>--platform &lt;code&gt;</code></td>
                                <td><code>-p</code></td>
                                <td><code>iphone</code></td>
                                <td><code>iphone</code> or <code>ipad</code> (case-insensitive)</td>
                            </tr>
                            <tr>
                                <td><code>--format &lt;format&gt;</code></td>
                                <td><code>-f</code></td>
                                <td><code>md</code></td>
                                <td><code>md</code>, <code>json</code>, or <code>csv</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        `;
    }
}
